import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type { IReportPermissionTarget } from '@op/services/report';
import { useOpenReportPermission } from '@op/hooks/useOpenReportPermission';
import ReportAccessPanel from './index';
import { parseReportPermissionTarget } from './utils/permissionNotification';

export type TReportPermissionNotificationMode = 'list' | 'create';

/** 报告创建成功时携带的权限目标数据 */
export type TReportPermissionTargetData = {
  object_type?: string;
  object_id?: number | string | null;
} | null;

interface IReportPermissionContextValue {
  notificationMode: TReportPermissionNotificationMode;
  openPermissionPanel: ReturnType<typeof useOpenReportPermission>['open'];
  openByTarget: (target: IReportPermissionTarget) => void;
  /** 暂存报告创建成功时的权限目标（写入即存，不触发渲染），待滚动通知弹出时读取 */
  setPendingReportTarget: (data?: TReportPermissionTargetData) => void;
  /** 读取并清空暂存的权限目标 */
  consumePendingReportTarget: () => IReportPermissionTarget | null;
}

const ReportPermissionContext =
  createContext<IReportPermissionContextValue | null>(null);

export function useReportPermissionNotify() {
  return useContext(ReportPermissionContext);
}

interface IReportPermissionProviderProps {
  children: React.ReactNode;
  notificationMode?: TReportPermissionNotificationMode;
  onSaved?: () => void;
}

export default function ReportPermissionProvider({
  children,
  notificationMode = 'create',
  onSaved,
}: IReportPermissionProviderProps) {
  const { visible, target, close, open, openByTarget } =
    useOpenReportPermission();

  // 用 ref 存放权限目标，写入不触发渲染
  const pendingReportTargetRef = useRef<IReportPermissionTarget | null>(null);

  const setPendingReportTarget = useCallback(
    (data?: TReportPermissionTargetData) => {
      const permissionTarget = parseReportPermissionTarget(data);
      if (!permissionTarget) return;
      pendingReportTargetRef.current = permissionTarget;
    },
    [],
  );

  const consumePendingReportTarget = useCallback(() => {
    const target = pendingReportTargetRef.current;
    pendingReportTargetRef.current = null;
    return target;
  }, []);

  // 离开页面时清空，避免残留
  useEffect(() => {
    return () => {
      pendingReportTargetRef.current = null;
    };
  }, []);

  const contextValue = useMemo<IReportPermissionContextValue>(
    () => ({
      notificationMode,
      openPermissionPanel: open,
      openByTarget,
      setPendingReportTarget,
      consumePendingReportTarget,
    }),
    [
      notificationMode,
      open,
      openByTarget,
      setPendingReportTarget,
      consumePendingReportTarget,
    ],
  );

  return (
    <ReportPermissionContext.Provider value={contextValue}>
      {children}
      <ReportAccessPanel
        visible={visible}
        target={target}
        onClose={close}
        onSaved={onSaved}
        notificationMode={notificationMode}
        onPermissionLinkClick={openByTarget}
      />
    </ReportPermissionContext.Provider>
  );
}
